import React, { useState, useEffect, useRef } from "react";
import { saApi } from "../../../api/superAdminApi";
import { ROLE_OPTIONS } from "../../../constants/roles";
import {
  Stack, FormControl, FormLabel, Input, Select, Button, HStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, useToast, Spacer,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter, useDisclosure,
} from "@chakra-ui/react";

const EditBUUserModal = ({
  isOpen,
  onClose,
  user,
  units,
  companyId,
  selectedUnitId,
  membershipsByUserId,
  onSaved,
}) => {
  const toast = useToast();
  const [editingUser, setEditingUser] = useState(null);
  const [selectedBusinessUnitIds, setSelectedBusinessUnitIds] = useState([]);
  const [originalBusinessUnitIds, setOriginalBusinessUnitIds] = useState([]);
  const [buRoles, setBuRoles] = useState({});
  const [originalBuRoles, setOriginalBuRoles] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const { isOpen: isPromoteOpen, onOpen: openPromote, onClose: closePromote } = useDisclosure();
  const [isPromoting, setIsPromoting] = useState(false);
  const cancelPromoteRef = useRef();

  // Initialize state when modal opens with a new user
  useEffect(() => {
    if (isOpen && user) {
      setEditingUser({ ...user });
      const memberships = membershipsByUserId?.[String(user.id)] || {};
      // memberships is { buId: role } — extract BU IDs and roles
      const buIds = Object.keys(memberships).map(String);
      // Ensure the current BU context is included
      if (selectedUnitId && !buIds.includes(String(selectedUnitId))) {
        buIds.push(String(selectedUnitId));
        memberships[String(selectedUnitId)] = user.bu_role || user.role || "Sales Representative";
      }
      setSelectedBusinessUnitIds(buIds);
      setOriginalBusinessUnitIds(buIds);
      setBuRoles({ ...memberships });
      setOriginalBuRoles({ ...memberships });
    }
  }, [isOpen, user, membershipsByUserId, selectedUnitId]);

  const handleClose = () => {
    setEditingUser(null);
    setIsUpdating(false);
    onClose();
  };

  const handleSave = async () => {
    if (!editingUser) return;
    try {
      setIsUpdating(true);
      const currentSelection = new Set(selectedBusinessUnitIds.map((id) => String(id)));
      const originalSelection = new Set(originalBusinessUnitIds.map((id) => String(id)));

      const addedBUs = [...currentSelection].filter((id) => !originalSelection.has(id));
      const removedBUs = [...originalSelection].filter((id) => !currentSelection.has(id));
      const keptBUs = [...currentSelection].filter((id) => originalSelection.has(id));
      const updatedRoleBUs = keptBUs.filter(
        (id) => buRoles[id] && originalBuRoles[id] && buRoles[id] !== originalBuRoles[id]
      );

      // Use bulk endpoint for atomic BU changes
      const hasBUChanges = addedBUs.length > 0 || removedBUs.length > 0 || updatedRoleBUs.length > 0;
      if (hasBUChanges) {
        await saApi.bulkUpdateUserBUs(companyId, editingUser.id, {
          add: addedBUs.map((buId) => ({
            business_unit_id: parseInt(buId),
            role: buRoles[buId] || "Sales Representative",
          })),
          remove: removedBUs.map((buId) => parseInt(buId)),
          update: updatedRoleBUs.map((buId) => ({
            business_unit_id: parseInt(buId),
            role: buRoles[buId],
          })),
        });
      }

      // Update non-BU fields via PATCH
      const payload = {
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        job_title: editingUser.job_title,
        reporting_manager: editingUser.reporting_manager,
      };
      await saApi.updateCompanyUser(companyId, editingUser.id, payload);

      toast({ title: "User updated", status: "success" });
      handleClose();
      if (onSaved) onSaved();
    } catch (e) {
      const message = e?.response?.data?.message || e?.response?.data?.detail || e?.message || "Update failed";
      toast({ title: "Update failed", description: message, status: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveFromBU = async () => {
    if (!selectedUnitId || !editingUser?.id) return;
    try {
      await saApi.removeUserFromBusinessUnit(companyId, selectedUnitId, editingUser.id);
      toast({ title: "User removed from business unit", status: "success" });
      handleClose();
      if (onSaved) onSaved();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Remove failed";
      toast({ title: "Failed to remove user", description: message, status: "error" });
    }
  };

  const handlePromote = async () => {
    if (!editingUser?.id) return;
    try {
      setIsPromoting(true);
      await saApi.updateCompanyUser(companyId, editingUser.id, { role: "Company Admin" });
      toast({ title: "User promoted to Company Admin", status: "success" });
      closePromote();
      handleClose();
      if (onSaved) onSaved();
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.detail || err?.message || "Promotion failed";
      toast({ title: "Promotion failed", description: message, status: "error" });
    } finally {
      setIsPromoting(false);
    }
  };

  const sortedUnits = (Array.isArray(units) ? units : []).slice().sort((a, b) =>
    String(a.unit_name || a.unit_code || a.id).localeCompare(
      String(b.unit_name || b.unit_code || b.id)
    )
  );

  const toggleBU = (buId) => {
    const id = String(buId);
    setSelectedBusinessUnitIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      } else {
        // Default role for newly added BU
        setBuRoles((r) => ({ ...r, [id]: "Sales Representative" }));
        return [...prev, id];
      }
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit user</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {editingUser && (
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>First name</FormLabel>
                <Input value={editingUser.first_name || ""} onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Last name</FormLabel>
                <Input value={editingUser.last_name || ""} onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Business units & roles</FormLabel>
                <Stack spacing={2}>
                  {sortedUnits.map((bu) => {
                    const id = String(bu.id);
                    const label = bu.unit_name || bu.unit_code || `${bu.id}`;
                    const isSelected = selectedBusinessUnitIds.includes(id);
                    return (
                      <HStack key={id} spacing={3}>
                        <Button
                          size="xs"
                          variant={isSelected ? "solid" : "outline"}
                          colorScheme={isSelected ? "teal" : "gray"}
                          onClick={() => toggleBU(id)}
                          minW="120px"
                          justifyContent="flex-start"
                        >
                          {isSelected ? "✓ " : ""}{label}
                        </Button>
                        {isSelected && (
                          <Select
                            size="sm"
                            value={buRoles[id] || "Sales Representative"}
                            onChange={(e) => setBuRoles((r) => ({ ...r, [id]: e.target.value }))}
                            maxW="200px"
                          >
                            {ROLE_OPTIONS.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </Select>
                        )}
                      </HStack>
                    );
                  })}
                </Stack>
              </FormControl>
              <FormControl>
                <FormLabel>Job title</FormLabel>
                <Input value={editingUser.job_title || ""} onChange={(e) => setEditingUser({ ...editingUser, job_title: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Reporting manager</FormLabel>
                <Input value={editingUser.reporting_manager || ""} onChange={(e) => setEditingUser({ ...editingUser, reporting_manager: e.target.value })} />
              </FormControl>
              <HStack pt={2}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  onClick={openPromote}
                >
                  Promote to Company Admin
                </Button>
                <Spacer />
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={handleRemoveFromBU}
                >
                  Remove from BU
                </Button>
              </HStack>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>Cancel</Button>
          <Button colorScheme="teal" onClick={handleSave} isLoading={isUpdating}>Save</Button>
        </ModalFooter>
      </ModalContent>

      <AlertDialog
        isOpen={isPromoteOpen}
        leastDestructiveRef={cancelPromoteRef}
        onClose={closePromote}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Promote to Company Admin
            </AlertDialogHeader>
            <AlertDialogBody>
              This will grant {editingUser?.first_name} {editingUser?.last_name} access to all
              business units as Company Admin. Their individual BU memberships will be removed.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelPromoteRef} onClick={closePromote}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handlePromote} isLoading={isPromoting} ml={3}>
                Promote
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default EditBUUserModal;
